import { Box, CircularProgress, Typography, Stack } from '@mui/material';

import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useLocation, useParams } from 'react-router-dom';

import { deleteRelatedEntity } from 'common/api';
import { IFiveWResponse } from 'common/api/type-def';
import { ReactComponent as Delete } from 'common/assets/images/delete-icon.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import InfoTooltip from 'common/components/Tooltip/Tooltip';
import { MODULES } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { TOOLTIP_TEXT } from 'common/constants/Tooltip';
import {
	setIsRelationshipMappingDeleted,
	setMappingDrawer,
} from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { infoIcon } from '../../../../../../common/assets/images';
import AccordionComp from '../../../../../../common/components/Accordion/AccordionComp';
import {
	getDescName,
	OVERVIEW_DETAILS_ACCORDIONS,
} from '../../../../../../common/constants/TaxonomyConstants';
import { RootState } from '../../../../../../common/store/AppStore';
import { IEntityData } from '../../../api/type-def';

import Description from './Description';
import Details from './Details';
import GrammarCheck from './GrammarCheck';
import RelationMappingTable from './RelationshipMappingTable/RelationshipMappingTable';
import Tags from './Tags';

import styles from './Overview.module.scss';

const { deleteFillColor } = styles;

const { OK, CREATED } = HTTP_STATUS_CODES;

interface IOverviewProps {
	entityData: IEntityData[];
	isLoading: boolean;
	fetchEntityData: any;
	validationsData: IFiveWResponse | undefined;
	controlValidationDataLoading: boolean;
	isScrollNeeded?: boolean;
	pageModule: string;
	isDeleteEnabled?: boolean;
}

const OverviewLayout = ({
	entityData,
	isLoading,
	fetchEntityData,
	validationsData,
	controlValidationDataLoading,
	isScrollNeeded,
	pageModule,
	isDeleteEnabled,
}: IOverviewProps) => {
	const location = useLocation();
	const { state } = location;
	const { groupId, subPartId } = useParams();
	const parentObjectId =
		state && state.entityId !== undefined
			? state.entityId
			: groupId || subPartId;

	const dispatch = useDispatch();
	const selectedEntity = useSelector(
		(state: RootState) => state.common.selectedEntityTypeData,
	);
	const { id, type, citationGroupId } = selectedEntity;
	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const [originalTags, setOriginalTags] = useState<any[]>([]);
	const [entityTags, setEntityTags] = useState<any[]>();
	const [refreshTags] = useState<boolean>(false);
	const [isTagUpdated, setIsTagUpdated] = useState<boolean>(false);
	const [expanded, setExpanded] = useState<string | true>('details');
	const [relMapExpanded, setRelMapExpanded] = useState<string | true>(
		'relationmapping',
	);
	const [descExpanded, setDescExpanded] = useState<string | true>(
		'description',
	);
	const [grammarCheckExpanded, setGrammarCheckExpanded] = useState<
		string | true
	>('grammarcheck');
	const [tagsExpanded, setTagsExpanded] = useState<string | true>('tags');
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	const handleAccordionChange =
		(panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
			const expandedValue = isExpanded ? panel : true;
			switch (panel) {
				case OVERVIEW_DETAILS_ACCORDIONS.RELATION_MAPPING:
					setRelMapExpanded(expandedValue);
					break;
				case OVERVIEW_DETAILS_ACCORDIONS.DESCRIPTION:
					setDescExpanded(expandedValue);
					break;
				case OVERVIEW_DETAILS_ACCORDIONS.TAGS:
					setTagsExpanded(expandedValue);
					break;
				case OVERVIEW_DETAILS_ACCORDIONS.DETAILS:
					setExpanded(expandedValue);
					break;
				case OVERVIEW_DETAILS_ACCORDIONS.GRAMMARCHECK:
					setGrammarCheckExpanded(expandedValue);
					break;
				default:
					setExpanded(expandedValue);
			}
		};

	const refreshTagHandler = () => {
		fetchEntityData();
	};
	const resetOnError = () => {
		setIsTagUpdated(false);
		setEntityTags(originalTags);
	};
	const checkIfDeleteDisabled = () => {
		if (selectedEntity.generateMode === 'AI') {
			return !isDeleteEnabled || selectedEntity.reviewStatus === 'Reviewed';
		}
		return (
			selectedEntity.createdBy !== loggedInUser?.userId ||
			selectedEntity.reviewStatus === 'Reviewed'
		);
	};

	const getDeleteBtnStatus = () =>
		pageModule === MODULES.REQUIREMENT_INVENTORY ||
		pageModule === MODULES.HORIZON_SCAN_AND_ALERTS
			? checkIfDeleteDisabled()
			: pageModule === MODULES.RISK_REFERENCE_TAXONOMY &&
			  selectedEntity.createdBy !== loggedInUser?.userId;

	useEffect(() => {
		if (entityData) {
			let tagsArray: string[] = [];
			const tags =
				entityData.filter((obj: any) => obj.label === 'Tags')[0]?.value ?? null;
			if (typeof tags === 'string' && tags.length > 0) {
				if (tags?.includes(',')) {
					tagsArray = tags.split(',');
				} else {
					tagsArray.push(tags);
				}
				const updatedTags = tagsArray.map((t: any, index) => ({
					id: index,
					tagText: t,
				}));
				setEntityTags(updatedTags);
				setOriginalTags(updatedTags);
			} else {
				setEntityTags([]);
				setOriginalTags([]);
			}
		}
	}, [entityData]);

	const deleteButtonClicked = async () => {
		let parentId = parentObjectId;
		let tempId = id;
		if (
			pageModule === MODULES.REQUIREMENT_INVENTORY ||
			pageModule === MODULES.HORIZON_SCAN_AND_ALERTS
		) {
			parentId = selectedEntity.parentId;
		}
		if (pageModule === MODULES.RISK_REFERENCE_TAXONOMY) {
			if (selectedEntity.swap) {
				tempId = parentId;
				parentId = id;
			}
		}
		setIsDeleting(true);
		const response = await deleteRelatedEntity(
			tempId,
			parentId || '',
			pageModule,
			loggedInUser?.email || '',
		);
		setIsDeleting(false);
		onDeleteResponse(response);
	};

	const onDeleteResponse = (response: any) => {
		if (response.statusCode === OK || response?.statusCode === CREATED) {
			dispatch(setMappingDrawer(!sideDrawerState));
			dispatch(setIsRelationshipMappingDeleted(true));
		}
	};

	return (
		<div
			className={styles['overview-container']}
			data-testid="overview-layout-container"
		>
			{isLoading ? (
				<CircularProgress
					size={90}
					className={styles['control-loader']}
					data-testid="overview-loader"
				/>
			) : (
				<>
					<AccordionComp
						className={styles['accordion-margin-btm']}
						expanded={expanded === 'details'}
						onChange={handleAccordionChange('details')}
						summary={
							type && (
								<Typography className={styles['accordion-title']}>
									Details
								</Typography>
							)
						}
						details={<Details entityData={entityData} />}
					/>
					<AccordionComp
						className={styles['accordion-margin-btm']}
						expanded={relMapExpanded === 'relationmapping'}
						onChange={handleAccordionChange('relationmapping')}
						summary={
							<>
								<Typography className={styles['accordion-title']}>
									{toSentenceCase('Relationship Mapping')}
								</Typography>
								<InfoTooltip
									className={styles['overview-info-tooltip']}
									title={TOOLTIP_TEXT.RELATIONSHIP_MAPPING}
								>
									<img src={infoIcon} alt="i" />
								</InfoTooltip>
							</>
						}
						details={
							<RelationMappingTable
								relationId={citationGroupId || id}
								relationType={type}
							/>
						}
					/>
					<AccordionComp
						isScrollNeeded={isScrollNeeded}
						className={styles['accordion-margin-btm']}
						expanded={descExpanded === 'description'}
						onChange={handleAccordionChange('description')}
						summary={
							<Typography
								className={styles['accordion-title']}
								data-testid="acc-des"
							>
								{getDescName(type)}
							</Typography>
						}
						details={<Description entityData={entityData} />}
					/>
					{controlValidationDataLoading && (
						<AccordionComp
							className={styles['accordion-margin-btm']}
							expanded={grammarCheckExpanded === 'grammarcheck'}
							onChange={handleAccordionChange('grammarcheck')}
							summary={
								<>
									<Typography className={styles['accordion-title']}>
										Control quality and grammar check
									</Typography>
									<img src={infoIcon} alt="i" />
								</>
							}
							details={
								controlValidationDataLoading &&
								validationsData !== undefined ? (
									<GrammarCheck
										data={validationsData}
										hideActionButton={false}
									/>
								) : (
									<CircularProgress data-testid="grammer-loader" />
								)
							}
						/>
					)}
					{pageModule !== MODULES.HORIZON_SCAN_AND_ALERTS && (
						<AccordionComp
							className={styles['accordion-margin-btm']}
							expanded={tagsExpanded === 'tags'}
							onChange={handleAccordionChange('tags')}
							summary={
								<>
									<Typography className={styles['accordion-title']}>
										Tags
									</Typography>
									<InfoTooltip
										className={styles['overview-info-tooltip']}
										title={TOOLTIP_TEXT.TAGS}
									>
										<img src={infoIcon} alt="i" />
									</InfoTooltip>
								</>
							}
							details={
								!refreshTags ? (
									<Tags
										entityTags={entityTags}
										setEntityTags={setEntityTags}
										setIsTagUpdated={setIsTagUpdated}
										isTagUpdated={isTagUpdated}
										refreshTagHandler={refreshTagHandler}
										resetOnError={resetOnError}
									/>
								) : (
									<Box sx={{ display: 'flex' }}>
										<CircularProgress />
									</Box>
								)
							}
						/>
					)}
					{(pageModule === MODULES.REQUIREMENT_INVENTORY ||
						pageModule === MODULES.RISK_REFERENCE_TAXONOMY ||
						pageModule === MODULES.HORIZON_SCAN_AND_ALERTS) && (
						<Stack
							direction="row"
							justifyContent="flex-end"
							className={styles['overview-btn-grp']}
							gap="1rem"
						>
							<ButtonComp
								btnType="secondary"
								disabled={getDeleteBtnStatus()}
								startIcon={
									!isDeleting && (
										<Box className={styles['overview-tab-button-icon']}>
											<Delete fill={deleteFillColor} />
										</Box>
									)
								}
								loading={isDeleting}
								clickHandler={deleteButtonClicked}
								data-testid="overview-delete-btn"
							>
								Delete
							</ButtonComp>
						</Stack>
					)}
				</>
			)}
		</div>
	);
};

export default OverviewLayout;
